import { Injectable, inject } from '@angular/core';
import { GtmService, ICustomWindow, WindowRefService } from '@sirius/common';
import { CartContentEvent, CartTrackingEventTypes, TrackingEventEcommerce } from 'commerce/models';

declare global {
    interface Window {
        dataLayer: any[];
    }
}

@Injectable({
    providedIn: 'root',
})
export class TrackingCommerceService {
    private _gtmSrv = inject(GtmService);
    private _windowRef = inject(WindowRefService);

    private get Window(): ICustomWindow | undefined {
        return this._windowRef.NativeWindow;
    }


    trackEvent(ecommerceTrackingEvent: TrackingEventEcommerce) {
        if (this.window) {
            this.window.dataLayer.push({ ecommerce: null });
        }

        this._gtmSrv.track(ecommerceTrackingEvent);
    }

    /**
     * For that special case where you need to determine the which of
     * the events to use based on the quantity change.
     *
     * The selected event will also be patched with the new quantity!
     *
     * @param originalQuantity the original quantity of the item
     * @param newQuantity the new quantity of the item
     * @param events the events to be resolved and tracked. At least one must be provided
     * @returns null if no event should be tracked,
     * otherwise the event type
     */
    resolveAndTrackCartContentEventType(
        originalQuantity: number,
        newQuantity: number,
        events: Partial<{ [key in CartTrackingEventTypes]: CartContentEvent }>
    ): CartTrackingEventTypes | null {
        if (Object.keys(events).length <= 0) {
            throw new Error('No events provided');
        }

        const difference = Math.abs(newQuantity - originalQuantity);
        if (difference === 0 || !Number.isInteger(difference)) {
            return null;
        }

        const eventType =
            newQuantity >= originalQuantity
                ? CartTrackingEventTypes.ADD_TO_CART
                : CartTrackingEventTypes.REMOVE_FROM_CART;

        const event = events[eventType];
        if (event) {
            let patchedEvent = this.patchCommerceItemQuantity(event, newQuantity);
            patchedEvent = this.recalculateCartValue(patchedEvent, newQuantity);
            this.trackEvent(patchedEvent);
        }

        return eventType;
    }

    private patchCommerceItemQuantity(commerceItemEvent: CartContentEvent, newQuantity: number) {
        if (typeof commerceItemEvent?.ecommerce?.items[0]?.quantity === 'number') {
            commerceItemEvent.ecommerce.items[0].quantity = newQuantity;
        }

        return commerceItemEvent;
    }

    /**
     * The specification for the ecommerce value is to be the total amount of all items in the event
     */
    private recalculateCartValue(commerceItemEvent: CartContentEvent, newQuantity: number) {
        const event = <CartContentEvent>commerceItemEvent;
        const price = event.ecommerce.items[0].price;
        let newValue = 0;

        if (typeof price === 'string') {
            // convert to number
            newValue = Number.parseFloat(price);
        }

        if (!Number.isNaN(newValue)) {
            newValue = newValue * newQuantity;

            // add decimal to keep convention
            if (Number.isInteger(newValue)) {
                commerceItemEvent.ecommerce.value = newValue.toFixed(1);
            }
        }

        return commerceItemEvent;
    }

    private get window(): ICustomWindow | undefined {
        return this._windowRef.NativeWindow;
    }

    private clearPreviousCommerceEvent() {
        if (this.Window && this.Window.dataLayer) {
            this.Window.dataLayer.push({ ecommerce: null });
        }
    }
}
